import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Box,
  Palette,
  Store,
  Map,
  FileBarChart,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";
import { useAppContext, IMPERSONATE_KEY, IMPERSONATE_EVENT } from "@/contexts/AppContext";
import { supabase } from "@/lib/supabase/client";
import DemoBanner from "@/components/DemoBanner";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

const TIER_BADGE: Record<string, { label: string; cls: string }> = {
  free:    { label: "Free",    cls: "bg-gray-800 text-gray-400 border-gray-700" },
  paid:    { label: "Paid",    cls: "bg-teal-900/50 text-teal-400 border-teal-700/60" },
  premium: { label: "Premium", cls: "bg-amber-900/40 text-amber-400 border-amber-700/60" },
};

// Nav items — `hasNotif` is resolved dynamically for Reports
const NAV_ITEMS = [
  { to: "/manufacturer/overview",   label: "Overview",    icon: LayoutDashboard, premium: false, reportsDot: false },
  { to: "/manufacturer/products",   label: "Products",    icon: Box,             premium: false, reportsDot: false },
  { to: "/manufacturer/variants",   label: "Variants",    icon: Palette,         premium: false, reportsDot: false },
  { to: "/manufacturer/dealers",    label: "Dealers",     icon: Store,           premium: false, reportsDot: false },
  { to: "/manufacturer/geography",  label: "Geography",   icon: Map,             premium: false, reportsDot: false },
  { to: "/manufacturer/reports",    label: "Reports",     icon: FileBarChart,    premium: false, reportsDot: true  },
  { to: "/manufacturer/benchmarks", label: "Benchmarks",  icon: TrendingUp,      premium: true,  reportsDot: false },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function ManufacturerLayout() {
  const navigate = useNavigate();
  const { signOut, manufacturerId: ctxManufacturerId, isAdmin } = useAppContext();

  const impersonatedId = isAdmin ? (localStorage.getItem(IMPERSONATE_KEY) ?? null) : null;
  const effectiveManufacturerId = impersonatedId ?? ctxManufacturerId;

  const [mfrName,   setMfrName]   = useState<string | null>(null);
  const [logoUrl,   setLogoUrl]   = useState<string | null>(null);
  const [tier,      setTier]      = useState<string | null>(null);
  const [showReportDot, setShowReportDot] = useState(false);

  useEffect(() => {
    if (!effectiveManufacturerId) return;

    // Manufacturer name + logo
    supabase
      .from("manufacturers")
      .select("name")
      .eq("id", effectiveManufacturerId)
      .single()
      .then(({ data }) => {
        const row = data as { name: string } | null;
        if (row?.name) setMfrName(row.name);
      });

    // Subscription: tier, logo_url, last_report_generated_at
    supabase
      .from("manufacturer_subscriptions")
      .select("tier, logo_url, last_report_generated_at")
      .eq("manufacturer_id", effectiveManufacturerId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        const row = data as { tier: string | null; logo_url?: string | null; last_report_generated_at?: string | null };
        if (row.tier) setTier(row.tier);
        if (row.logo_url) setLogoUrl(row.logo_url);
        // Show notification dot if report was never generated or older than 30 days
        if (!row.last_report_generated_at) {
          setShowReportDot(true);
        } else {
          const diffDays = (Date.now() - new Date(row.last_report_generated_at).getTime()) / 86_400_000;
          setShowReportDot(diffDays > 30);
        }
      });
  }, [effectiveManufacturerId]);

  async function handleSignOut() {
    await signOut();
    navigate("/");
  }

  function exitImpersonation() {
    localStorage.removeItem(IMPERSONATE_KEY);
    window.dispatchEvent(new Event(IMPERSONATE_EVENT));
    navigate("/admin/manufacturers");
  }

  const tierMeta = tier ? (TIER_BADGE[tier] ?? TIER_BADGE.free) : null;

  return (
    <div className="flex flex-col min-h-screen bg-gray-950 text-gray-100">
      <DemoBanner />
      {/* Impersonation banner */}
      {impersonatedId && (
        <div className="shrink-0 bg-yellow-900/60 border-b border-yellow-700/60 px-6 py-2 flex items-center gap-3">
          <AlertTriangle className="h-4 w-4 text-yellow-400 shrink-0" />
          <span className="text-sm text-yellow-200">
            Impersonating <span className="font-semibold">{mfrName ?? "…"}</span>
          </span>
          <button
            onClick={exitImpersonation}
            className="ml-auto text-xs rounded-md border border-yellow-600 px-3 py-1 text-yellow-300 hover:bg-yellow-800/40 transition"
          >
            Exit
          </button>
        </div>
      )}

      {/* Header */}
      <header className="h-14 shrink-0 border-b border-gray-800 bg-gray-900 flex items-center px-6 gap-3">
        <span className="text-sm font-semibold text-gray-100">Manufacturer Portal</span>
        {mfrName && (
          <>
            <span className="text-gray-700">—</span>
            <span className="text-sm text-gray-400">{mfrName}</span>
          </>
        )}
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 border-r border-gray-800 bg-gray-900 flex flex-col">

          {/* Sidebar header: logo/avatar + name */}
          {mfrName && (
            <div className="px-4 py-4 border-b border-gray-800 flex items-center gap-3">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={mfrName}
                  className="h-9 w-9 rounded-full object-cover shrink-0"
                />
              ) : (
                <div className="h-10 w-10 rounded-full bg-teal-500 flex items-center justify-center text-sm font-semibold text-white shrink-0 select-none">
                  {initials(mfrName)}
                </div>
              )}
              <span className="text-sm font-medium text-gray-200 truncate">{mfrName}</span>
            </div>
          )}

          <nav className="flex-1 py-3">
            {NAV_ITEMS.map(({ to, label, icon: Icon, premium, reportsDot }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-2.5 px-4 py-2 text-sm border-b-2 transition-colors",
                    isActive
                      ? "border-teal-400 text-teal-400 font-medium"
                      : "border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-700",
                  ].join(" ")
                }
              >
                <Icon size={15} className="shrink-0" />
                <span className="flex-1">{label}</span>
                {reportsDot && showReportDot && (
                  <span className="h-2 w-2 rounded-full bg-teal-500 shrink-0" title="Monthly report not yet generated" />
                )}
                {premium && (
                  <span className="rounded-full bg-teal-900/60 text-teal-400 border border-teal-700/60 text-[10px] font-medium px-1.5 py-0.5 leading-none">
                    Premium
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Sidebar footer: tier badge + sign out */}
          <div className="border-t border-gray-800 px-4 py-4 space-y-2">
            {mfrName && (
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-500 truncate flex-1">{mfrName}</p>
                {tierMeta && (
                  <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium leading-none ${tierMeta.cls}`}>
                    {tierMeta.label}
                  </span>
                )}
              </div>
            )}
            <button
              onClick={handleSignOut}
              className="w-full text-left text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              Sign out
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto p-6">
          <Outlet context={{ effectiveManufacturerId }} />
        </main>
      </div>
    </div>
  );
}

/** Helper hook for pages that need the effective manufacturer ID (respects impersonation). */
export function useEffectiveManufacturerId(): string | null {
  const { manufacturerId: ctxId, isAdmin } = useAppContext();
  const impersonated = isAdmin ? (localStorage.getItem(IMPERSONATE_KEY) ?? null) : null;
  return impersonated ?? ctxId;
}
